import { useState, useMemo, useId } from 'react';
import styled from 'styled-components';
import { InputText } from 'ui/inputs/inputText';
import { FormField } from 'ui/formField';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES } from 'ui/modal';
import { PrimaryButton } from 'ui/button';
import { spacing } from 'ui/theme';
import { CREATE_GUIDELINES } from '../constants';
import { isAlphanumeric } from '../helpers';

const UL = styled.ul`
  display: flex;
  padding: 0;
  flex-direction: column;
  gap: ${spacing.quarter};
`;

type CreateCatalogModalProps = {
  catalogNames: string[];
  closeModal: () => void;
  isOpen: boolean;
  isSubmitting?: boolean;
  onCreate: (catalogName: string) => void;
};

export const CreateCatalogModal = ({
  catalogNames = [],
  closeModal,
  isOpen,
  isSubmitting,
  onCreate,
}: CreateCatalogModalProps) => {
  const [catalogName, setCatalogName] = useState('');
  const modalId = useId();

  const handleCloseModal = () => {
    setCatalogName('');
    closeModal();
  };

  const handleSubmit = () => {
    onCreate(catalogName);
    handleCloseModal();
  };

  const isNotAlphanumericCatalogName = !isAlphanumeric(catalogName);

  const isExistingCatalogName = catalogNames.includes(catalogName.toLowerCase());

  const isTooLongCatalogName = catalogName.length > 400;

  const isDisabled = useMemo(
    () =>
      isSubmitting ||
      catalogName.length === 0 ||
      isNotAlphanumericCatalogName ||
      isExistingCatalogName ||
      isTooLongCatalogName,
    [catalogName, isSubmitting],
  );

  const validation = {
    isTooLong: isTooLongCatalogName,
    isNotUnique: isExistingCatalogName,
    isNotAlphaNumeric: isNotAlphanumericCatalogName && catalogName.length > 0,
  };

  const shouldDisplayGuidelines = Object.values(validation).includes(true);

  const guidelines = CREATE_GUIDELINES.filter(
    (guideline) => validation[guideline.isDisplayed as keyof typeof validation],
  );

  return isOpen ? (
    <Modal
      id={modalId}
      data-test="CreateCatalogModal"
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader>Create new catalog</ModalHeader>
      <ModalBody>
        <FormField
          hasError={shouldDisplayGuidelines}
          inputId="create-catalog-modal-input"
          isRequired
          labelText="Name"
          errorText={
            <>
              <p>The catalog name:</p>
              <UL>
                {guidelines.map((guideline, i) => (
                  <li key={i}>{guideline.children}</li>
                ))}
              </UL>
            </>
          }
        >
          <InputText
            autoFocus
            id="create-catalog-modal-input"
            data-test="create-catalog-modal-input"
            onChange={(value) => setCatalogName(value)}
            value={catalogName}
          />
        </FormField>
      </ModalBody>
      <ModalFooter onClose={closeModal}>
        <PrimaryButton
          onClick={handleSubmit}
          isDisabled={isDisabled}
          data-test="create-catalog-modal-submit"
        >
          Create
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  ) : null;
};
